// Newsletter · Re-post packs (1.56; docs/specs/P1_attract_v3_ctas_and_legs.md section 5). Each issue, ready to paste
// where the niche reads (LinkedIn newsletter, Substack, Medium, a trade-press pitch). Ogma never posts a pack: the
// owner copies it, posts it, and marks it posted with the post's public address. Every pack says where it was first
// published and ends with a tracked subscribe link back to the business's own list.
import { useState } from "react";
import { useCrm } from "../../lib/session";
import { useAsync } from "../../lib/useAsync";
import { Async, Notice } from "../../components/ui";
import { Card } from "../../components/kit";
import { fmtDate, humanise, webUrl } from "../../lib/format";
import { TARGET_LABEL, isPostUrl, packPlainText, type SyndicationPack } from "../../lib/syndication";
import type { HumanTask } from "../../lib/types";

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** Copies text; for an HTML pack, rich text too, so it pastes with its headings and links. */
async function copy(text: string, html?: string): Promise<void> {
  const clip = navigator.clipboard;
  if (!clip) return;
  if (html && typeof ClipboardItem !== "undefined" && clip.write) {
    try {
      await clip.write([new ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([text], { type: "text/plain" }) })]);
      return;
    } catch {
      // fall back to plain text
    }
  }
  await clip.writeText(text).catch(() => undefined);
}

function CopyButton({ label, text, html }: { label: string; text: string; html?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-small btn-quiet"
      aria-label={label}
      onClick={() => {
        void copy(text, html);
        setDone(true);
        window.setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? "Copied" : label.replace(/ for .*$/, "")}
    </button>
  );
}

function PackCard({ p, onChanged }: { p: SyndicationPack; onChanged: () => void }) {
  const crm = useCrm();
  const [url, setUrl] = useState(p.posted_url ?? "");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  const target = TARGET_LABEL[p.target] ?? humanise(p.target);
  const name = `${target}: ${p.issue_title ?? p.pack.title}`;
  const posted = webUrl(p.posted_url);
  const plain = packPlainText(p.pack);
  const original = webUrl(p.pack.canonical_url);

  async function markPosted() {
    if (!isPostUrl(url)) {
      setNote({ text: "Paste the post's public address (starting https://) to mark it posted.", tone: "bad" });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      await crm.markSyndicationPosted(p.task_id, url);
      setNote({ text: "Marked posted. Subscribers it brings in are counted on its link.", tone: "ok" });
      onChanged();
    } catch (e) {
      setNote({ text: errorText(e), tone: "bad" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="listing" aria-label={name}>
      <div className="listing-head">
        <strong>{target}</strong>
        <span className="muted small">
          {p.issue_title ?? p.pack.title}
          {p.issue_published_at ? ` · sent ${fmtDate(p.issue_published_at)}` : ""}
        </span>
        <span className={`chip chip-${p.status === "posted" ? "ok" : "plain"}`}>{p.status === "posted" ? "Posted" : p.status === "done" ? "Done" : "Ready to paste"}</span>
        {posted && (
          <a className="small" href={posted} target="_blank" rel="noreferrer">
            See the post
          </a>
        )}
      </div>
      <p className="small">
        <span className="listing-value">{p.pack.title}</span> <CopyButton label={`Copy title for ${name}`} text={p.pack.title} />{" "}
        <CopyButton label={`Copy body for ${name}`} text={plain} html={p.pack.format === "html" ? p.pack.body : undefined} />
      </p>
      <p className="muted small">
        {p.pack.canonical_note}
        {original && (
          <>
            {" · "}
            <a href={original} target="_blank" rel="noreferrer">
              the original
            </a>
          </>
        )}
        {p.pack.cta?.label ? ` · ends with “${p.pack.cta.label}”, a tracked link back to your list` : ""}
        {p.pack.cover_image ? " · cover image: your issue's card" : ""}
      </p>
      {p.pack.how_to && <p className="small">{p.pack.how_to}</p>}
      {/* card-check: not a card (the pack's full text, folded inside its row) */}
      <details className="listing-checklist">
        <summary>Preview</summary>
        <pre className="small" style={{ whiteSpace: "pre-wrap" }}>
          {plain}
        </pre>
      </details>
      <form
        className="listing-done"
        onSubmit={(e) => {
          e.preventDefault();
          void markPosted();
        }}
      >
        <label className="field">
          <span className="field-label">Post address</span>
          <input type="url" inputMode="url" placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} aria-label={`Post address for ${name}`} />
        </label>
        <button type="submit" className="btn btn-small btn-primary" disabled={busy}>
          {p.status === "posted" ? "Update address" : "Mark posted"}
        </button>
      </form>
      <Notice text={note?.text ?? null} tone={note?.tone} />
    </li>
  );
}

/** The Newsletter page's "Re-post packs" section. Hidden while there are none (newsletter.syndicate is empty). */
export function SyndicationPacks() {
  const crm = useCrm();
  const rows = useAsync(() => crm.syndicationPacks(), [crm]);
  if (rows.data && rows.data.length === 0) return null;
  const ready = (rows.data ?? []).filter((p) => p.status !== "posted" && p.status !== "done").length;
  const subtitle = !rows.data ? "Loading" : ready ? `${ready} ready to paste, of ${rows.data.length}` : `All ${rows.data.length} posted`;
  return (
    <Card id="repost-packs" level={2} title="Re-post packs" subtitle={subtitle} waiting={ready > 0}>
      <p className="muted small">
        Your list on your own site stays the home of the newsletter. These packs put each issue where your readers already are, marked as first published on
        your site and ending with a link back to your list. Ogma never posts them for you: paste each one, then mark it posted with its address.
      </p>
      <Async state={rows} label="Loading re-post packs">
        {(d) => (
          <ul className="listings">
            {d.map((p) => (
              <PackCard key={p.task_id} p={p} onChanged={rows.reload} />
            ))}
          </ul>
        )}
      </Async>
    </Card>
  );
}

/** The pack as a task card on Home (kind syndication_pack): the same copy buttons and "Mark posted". */
export function SyndicationTask({ task, onDone }: { crm: unknown; task: HumanTask; onDone: () => void }) {
  const p = task.payload as unknown as { content_item_id: string; target: SyndicationPack["target"]; title?: string; pack: SyndicationPack["pack"] };
  const pack: SyndicationPack = {
    task_id: task.id,
    content_item_id: p.content_item_id,
    issue_title: p.title ?? p.pack?.title ?? null,
    content_kind: null,
    issue_published_at: null,
    target: p.target,
    status: "ready",
    pack: p.pack,
    posted_url: null,
    posted_at: null,
    created_at: task.created_at,
  };
  return (
    <ul className="listings">
      <PackCard p={pack} onChanged={onDone} />
    </ul>
  );
}
